<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue';
  import { useTableEditorStore } from '../../../model/table-editor.store';
  import { Select, SelectContent, SelectItem, SelectTrigger, IconImage } from '@/shared/ui';
  import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
  import { sanitizeColor, clampNumber } from '../../../lib/color-sanitizer';
  import {
    detectBorderPattern,
    extractBorderProperties,
    BORDER_PATTERNS,
    type BorderPattern,
    type BorderProperties,
  } from '../../../lib/cell-border-utils';
  import TableBorderPatternAllIcon from '@/shared/assets/icons/TableBorderPatternAllIcon.svg';
  import TableBorderPatternTopIcon from '@/shared/assets/icons/TableBorderPatternTopIcon.svg';
  import TableBorderPatternRightIcon from '@/shared/assets/icons/TableBorderPatternRightIcon.svg';
  import TableBorderPatternBottomIcon from '@/shared/assets/icons/TableBorderPatternBottomIcon.svg';
  import TableBorderPatternLeftIcon from '@/shared/assets/icons/TableBorderPatternLeftIcon.svg';
  import TableBorderPatternTopBottomIcon from '@/shared/assets/icons/TableBorderPatternTopBottomIcon.svg';
  import TableBorderPatternLeftRightIcon from '@/shared/assets/icons/TableBorderPatternLeftRightIcon.svg';
  import TableBorderPatternNoneIcon from '@/shared/assets/icons/TableBorderPatternNoneIcon.svg';
  import TableFillColorActionIcon from '@/shared/assets/icons/TableFillColorActionIcon.svg';
  import TableBrushIcon from '@/shared/assets/icons/TableBrushIcon.svg';
  import BorderStyleSingleIcon from '@/shared/assets/icons/BorderStyleSingleIcon.svg';
  import type { CellContentAlignment } from '../../../model/form.types';
  import { ALIGNMENT_OPTIONS } from '../../../lib/cell-alignment';

  const tableEditorStore = useTableEditorStore();

  const selectedCellData = computed(() => tableEditorStore.selectedCellData);
  const hasMultipleSelection = computed(() => tableEditorStore.hasMultipleSelection);

  const backgroundColor = computed({
    get: () => {
      const cellData = hasMultipleSelection.value
        ? tableEditorStore.selectedCellsData[0]
        : selectedCellData.value;
      return cellData?.properties?.backgroundColor ?? '#ffffff';
    },
    set: (value: string) => {
      if (tableEditorStore.selectedCells.length === 0) return;
      const sanitized = sanitizeColor(value, '#ffffff');
      if (hasMultipleSelection.value) {
        tableEditorStore.updateSelectedCellsProperty('backgroundColor', sanitized);
      } else if (selectedCellData.value) {
        tableEditorStore.updateCellProperty('backgroundColor', sanitized);
      }
    },
  });

  const padding = computed({
    get: () => {
      const cellData = hasMultipleSelection.value
        ? tableEditorStore.selectedCellsData[0]
        : selectedCellData.value;
      return cellData?.properties?.padding ?? 0;
    },
    set: (value: number) => {
      if (tableEditorStore.selectedCells.length === 0) return;
      const clamped = clampNumber(value, 0, 50);
      if (hasMultipleSelection.value) {
        tableEditorStore.updateSelectedCellsProperty('padding', clamped);
      } else if (selectedCellData.value) {
        tableEditorStore.updateCellProperty('padding', clamped);
      }
    },
  });

  const alignment = computed<CellContentAlignment>({
    get: () => {
      const cellData = hasMultipleSelection.value
        ? tableEditorStore.selectedCellsData[0]
        : selectedCellData.value;
      return cellData?.properties?.alignment ?? 'left';
    },
    set: (value: CellContentAlignment) => {
      if (tableEditorStore.selectedCells.length === 0) return;
      if (hasMultipleSelection.value) {
        tableEditorStore.updateSelectedCellsProperty('alignment', value);
      } else if (selectedCellData.value) {
        tableEditorStore.updateCellProperty('alignment', value);
      }
    },
  });

  const paddingRangeEl = ref<HTMLInputElement | null>(null);
  const paddingTrackWidth = ref(192);

  onMounted(() => {
    if (paddingRangeEl.value) paddingTrackWidth.value = paddingRangeEl.value.offsetWidth;
  });

  function thumbCorrectedFill(value: number, min: number, max: number, trackWidth: number): string {
    const raw = (value - min) / (max - min);
    const corrected = ((raw * (trackWidth - 16) + 8) / trackWidth) * 100;
    return `${corrected}%`;
  }

  const paddingProgress = computed(() =>
    thumbCorrectedFill(padding.value, 0, 50, paddingTrackWidth.value)
  );

  const paddingInputText = ref(String(padding.value));

  watch(padding, (v) => {
    paddingInputText.value = String(v);
  });

  function onPaddingTextInput(e: Event): void {
    paddingInputText.value = (e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 3);
  }

  function commitPaddingValue(): void {
    const parsed = Number.parseInt(paddingInputText.value, 10);
    if (Number.isNaN(parsed) || paddingInputText.value === '') {
      padding.value = 0;
      paddingInputText.value = '0';
    } else {
      const clamped = clampNumber(parsed, 0, 50);
      padding.value = clamped;
      paddingInputText.value = String(clamped);
    }
  }

  const selectedPattern = ref<BorderPattern | null>(null);

  const currentPattern = computed(() => {
    const cellData = hasMultipleSelection.value
      ? tableEditorStore.selectedCellsData[0]
      : selectedCellData.value;
    return selectedPattern.value ?? detectBorderPattern(cellData?.properties) ?? 'none';
  });

  watch(
    () => tableEditorStore.selectedCells.length,
    () => {
      selectedPattern.value = null;
    }
  );

  const applyPattern = (pattern: BorderPattern, properties: BorderProperties) => {
    if (tableEditorStore.selectedCells.length === 0) return;
    tableEditorStore.updateCellBordersPattern(pattern, properties);
    selectedPattern.value = pattern;
  };

  const globalBorderProperties = computed(() => {
    const cellData = hasMultipleSelection.value
      ? tableEditorStore.selectedCellsData[0]
      : selectedCellData.value;
    return extractBorderProperties(cellData?.properties);
  });

  const globalBorderColor = computed({
    get: () => globalBorderProperties.value.color,
    set: (value: string) => {
      if (tableEditorStore.selectedCells.length === 0) return;
      const sanitized = sanitizeColor(value, '#e3e3e3');
      applyPattern(currentPattern.value, {
        color: sanitized,
        width: globalBorderWidth.value,
        style: globalBorderStyle.value,
      });
    },
  });

  const globalBorderWidth = computed({
    get: () => globalBorderProperties.value.width,
    set: (value: number) => {
      if (tableEditorStore.selectedCells.length === 0) return;
      applyPattern(currentPattern.value, {
        color: globalBorderColor.value,
        width: clampNumber(value, 1, 5),
        style: globalBorderStyle.value,
      });
    },
  });

  const globalBorderStyle = computed({
    get: () => globalBorderProperties.value.style,
    set: (value: 'none' | 'solid' | 'dashed' | 'dotted') => {
      if (tableEditorStore.selectedCells.length === 0) return;
      applyPattern(currentPattern.value, {
        color: globalBorderColor.value,
        width: globalBorderWidth.value,
        style: value,
      });
    },
  });

  const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

  const bgColorHex = ref(backgroundColor.value.replace('#', ''));
  const bgColorError = ref('');

  watch(
    () => backgroundColor.value,
    (val) => {
      bgColorHex.value = val.replace('#', '');
      bgColorError.value = '';
    }
  );

  function onBgColorInput(e: Event) {
    const raw = (e.target as HTMLInputElement).value;
    bgColorHex.value = raw;
    const hex = '#' + raw;
    if (HEX_COLOR_REGEX.test(hex)) {
      bgColorError.value = '';
      backgroundColor.value = hex;
    } else {
      bgColorError.value = raw.length > 0 ? 'Cor inválida (use formato #RRGGBB)' : '';
    }
  }

  const borderColorHex = ref(globalBorderColor.value.replace('#', ''));
  const borderColorError = ref('');

  watch(
    () => globalBorderColor.value,
    (val) => {
      borderColorHex.value = val.replace('#', '');
      borderColorError.value = '';
    }
  );

  function onBorderColorInput(e: Event) {
    const raw = (e.target as HTMLInputElement).value;
    borderColorHex.value = raw;
    const hex = '#' + raw;
    if (HEX_COLOR_REGEX.test(hex)) {
      borderColorError.value = '';
      globalBorderColor.value = hex;
    } else {
      borderColorError.value = raw.length > 0 ? 'Cor inválida (use formato #RRGGBB)' : '';
    }
  }

  const patternIcons: Record<BorderPattern, string | object> = {
    all: TableBorderPatternAllIcon,
    top: TableBorderPatternTopIcon,
    right: TableBorderPatternRightIcon,
    bottom: TableBorderPatternBottomIcon,
    left: TableBorderPatternLeftIcon,
    'top-bottom': TableBorderPatternTopBottomIcon,
    'left-right': TableBorderPatternLeftRightIcon,
    none: TableBorderPatternNoneIcon,
  };

  const borderWidthOptions = ['1', '2', '3', '4', '5'];
  const borderStyleOptions: Array<{ value: 'solid' | 'dashed' | 'dotted'; label: string }> = [
    { value: 'solid', label: 'Sólida' },
    { value: 'dashed', label: 'Tracejada' },
    { value: 'dotted', label: 'Pontilhada' },
  ];

  const borderWidthState = computed({
    get: () => String(globalBorderWidth.value),
    set: (v: string) => {
      globalBorderWidth.value = Number(v);
    },
  });

  const borderStyleState = computed({
    get: () => globalBorderStyle.value,
    set: (v: string) => {
      globalBorderStyle.value = v as 'solid' | 'dashed' | 'dotted';
    },
  });

  const patternOpen = ref(false);

  const getBorderStylePreviewClass = (style: string) => `style-preview--${style}`;
</script>

<template>
  <div
    v-if="tableEditorStore.selectedCells.length > 0"
    class="flex flex-col"
  >
    <!-- Espaçamento -->
    <div class="border-b border-gray-02 py-4 flex flex-col gap-2">
      <p class="text-sm font-semibold text-gray-11">
        Espaçamento
      </p>
      <div class="flex flex-col gap-4">
        <div class="flex items-center justify-between">
          <p class="text-sm text-gray-11">
            Interno
          </p>
          <div
            class="bg-white-01 border border-gray-02 flex h-[42px] items-center justify-between overflow-hidden pl-4 pr-3 py-2.5 rounded-lg shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] w-[74px]"
          >
            <input
              type="text"
              inputmode="numeric"
              maxlength="3"
              min="0"
              max="50"
              :value="paddingInputText"
              class="border-0 outline-none p-0 w-full text-sm text-gray-07 bg-transparent"
              @focus="($event.target as HTMLInputElement).select()"
              @input="onPaddingTextInput"
              @blur="commitPaddingValue"
              @keydown.enter="commitPaddingValue"
            >
            <span class="text-gray-03 text-sm shrink-0">px</span>
          </div>
        </div>
        <input
          ref="paddingRangeEl"
          type="range"
          min="0"
          max="50"
          :value="padding"
          :style="{ '--val': paddingProgress }"
          class="slider-track w-full"
          @input="(e) => (padding = Number((e.target as HTMLInputElement).value))"
        >
      </div>
    </div>

    <!-- Cor de preenchimento -->
    <div class="border-b border-gray-02 py-4 flex flex-col gap-[10px]">
      <p class="text-sm text-gray-11">
        Cor de preenchimento
      </p>
      <div class="flex gap-2 h-[42px] items-center">
        <div class="flex items-center justify-center size-8 shrink-0">
          <IconImage
            :src="TableFillColorActionIcon"
            alt="Cor de preenchimento"
            class="size-[14px] text-gray-03"
          />
        </div>
        <div
          class="flex-1 flex items-center gap-2 h-[42px] bg-white-01 border border-gray-02 rounded-lg pl-4 pr-3 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] overflow-hidden"
        >
          <div
            class="relative size-6 rounded border border-gray-02 shrink-0 cursor-pointer overflow-hidden"
            :style="{ backgroundColor }"
          >
            <input
              type="color"
              :value="backgroundColor"
              class="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
              @input="(e) => (backgroundColor = (e.target as HTMLInputElement).value)"
            >
          </div>
          <span class="text-gray-03 font-medium text-sm shrink-0">#</span>
          <input
            :value="bgColorHex"
            type="text"
            class="flex-1 text-sm text-gray-07 bg-transparent outline-none border-none min-w-0"
            placeholder="000000"
            @input="onBgColorInput"
          >
        </div>
      </div>
      <p
        v-if="bgColorError"
        class="text-sm text-red-500 font-medium"
      >
        {{ bgColorError }}
      </p>
    </div>

    <!-- Bordas -->
    <div class="border-b border-gray-02 py-4 flex flex-col gap-[14px]">
      <p class="text-sm font-semibold text-gray-11">
        Bordas
      </p>

      <div class="border-menus">
        <!-- Border pattern -->
        <Popover v-model:open="patternOpen">
          <PopoverTrigger as-child>
            <button
              type="button"
              class="compact-trigger"
            >
              <IconImage
                :src="patternIcons[currentPattern]"
                alt="Padrão de borda"
                class="text-gray-03"
              />
            </button>
          </PopoverTrigger>
          <PopoverContent
            class="p-2 w-auto"
            align="start"
          >
            <div class="grid grid-cols-4 gap-0.5">
              <button
                v-for="p in BORDER_PATTERNS"
                :key="p.value"
                type="button"
                :class="[
                  'size-8 flex items-center justify-center rounded',
                  currentPattern === p.value ? 'bg-[var(--theme-secondary-tint)]' : 'hover:bg-gray-12',
                ]"
                :title="p.label"
                @click="
                  applyPattern(p.value, {
                    color: globalBorderColor,
                    width: globalBorderWidth,
                    style: globalBorderStyle,
                  });
                  patternOpen = false;
                "
              >
                <IconImage
                  :src="patternIcons[p.value]"
                  alt="Padrão"
                  :class="[currentPattern === p.value ? 'text-[var(--theme-secondary-ink)]' : 'text-gray-03']"
                />
              </button>
            </div>
          </PopoverContent>
        </Popover>

        <!-- Border width -->
        <Select v-model="borderWidthState">
          <SelectTrigger class="compact-trigger compact-trigger--width">
            <IconImage
              :src="BorderStyleSingleIcon"
              alt="Espessura da borda"
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="option in borderWidthOptions"
              :key="option"
              :value="option"
            >
              {{ option }}px
            </SelectItem>
          </SelectContent>
        </Select>

        <!-- Border style -->
        <Select v-model="borderStyleState">
          <SelectTrigger class="compact-trigger compact-trigger--style">
            <div
              class="style-preview-stack"
              aria-hidden="true"
            >
              <span class="style-preview style-preview--solid" />
              <span class="style-preview style-preview--dashed" />
              <span class="style-preview style-preview--dotted" />
            </div>
            <span class="sr-only">Estilo de borda atual</span>
          </SelectTrigger>
          <SelectContent
            class="style-select-content !w-[100px] !min-w-[100px] !max-w-[100px] rounded-2xl p-0"
            position="popper"
            side="bottom"
            align="center"
            :side-offset="6"
          >
            <SelectItem
              v-for="option in borderStyleOptions"
              :key="option.value"
              :value="option.value"
              class="style-select-item"
            >
              <div class="style-option-item">
                <span
                  class="style-preview"
                  :class="getBorderStylePreviewClass(option.value)"
                  aria-hidden="true"
                />
                <span class="sr-only">{{ option.label }}</span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <!-- Cor da borda -->
      <div class="flex flex-col gap-2">
        <p class="text-sm text-gray-11">
          Cor
        </p>
        <div class="flex gap-2 items-center">
          <div class="flex items-center justify-center size-8 shrink-0">
            <IconImage
              :src="TableBrushIcon"
              alt="Cor da borda"
              class="size-5 text-gray-03"
            />
          </div>
          <div
            class="flex-1 flex items-center gap-2 h-[42px] bg-white-01 border border-gray-02 rounded-lg pl-4 pr-3 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] overflow-hidden"
          >
            <div
              class="relative size-6 rounded border border-gray-02 shrink-0 cursor-pointer overflow-hidden"
              :style="{ backgroundColor: globalBorderColor }"
            >
              <input
                type="color"
                :value="globalBorderColor"
                class="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                @input="(e) => (globalBorderColor = (e.target as HTMLInputElement).value)"
              >
            </div>
            <span class="text-gray-03 font-medium text-sm shrink-0">#</span>
            <input
              :value="borderColorHex"
              type="text"
              class="flex-1 text-sm text-gray-07 bg-transparent outline-none border-none min-w-0"
              placeholder="000000"
              @input="onBorderColorInput"
            >
          </div>
        </div>
        <p
          v-if="borderColorError"
          class="text-sm text-red-500 font-medium"
        >
          {{ borderColorError }}
        </p>
      </div>
    </div>

    <!-- Alinhamento -->
    <div class="py-4 flex flex-col gap-[14px]">
      <p class="text-sm font-semibold text-gray-11">
        Alinhamento
      </p>
      <div class="flex h-[42px] items-stretch gap-0.5 rounded-lg bg-gray-02 p-0.5">
        <button
          v-for="option in ALIGNMENT_OPTIONS"
          :key="option.value"
          v-tippy="{
            content: option.label,
            placement: 'top',
            theme: 'custom',
            trigger: 'mouseenter',
            hideOnClick: true,
          }"
          type="button"
          :class="[
            'flex flex-1 items-center justify-center rounded-md p-1 transition-colors text-gray-03 [&_svg]:w-5 [&_svg]:h-5 [&_svg]:overflow-visible',
            alignment === option.value
              ? 'bg-white-01 shadow-[0px_1px_1px_0px_rgba(16,24,40,0.05)]'
              : 'hover:bg-white-01/40',
          ]"
          :aria-label="option.label"
          :aria-pressed="alignment === option.value"
          @click="alignment = option.value"
        >
          <IconImage
            :src="option.icon"
            :alt="option.label"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .border-menus {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 6px;
    height: 42px;
  }

  .compact-trigger {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 0 6px;
  }

  .style-option-item {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0;
  }

  .style-preview-stack {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .style-preview {
    width: 24px;
    border-top-width: 2px;
    border-top-color: #475467;
    border-top-style: solid;
    display: block;
  }

  .style-option-item .style-preview {
    width: 36px;
  }

  .style-preview--solid {
    border-top-style: solid;
  }

  .style-preview--dashed {
    border-top-style: dashed;
  }

  .style-preview--dotted {
    border-top-style: dotted;
  }

  .slider-track {
    -webkit-appearance: none;
    appearance: none;
    height: 4px;
    background: linear-gradient(to right, var(--theme-secondary) var(--val, 0%), var(--color-gray-02) var(--val, 0%));
    border-radius: 2px;
    outline: none;
    cursor: pointer;
  }

  .slider-track::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    background: white;
    border-radius: 8px;
    box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.25);
    cursor: pointer;
  }

  .slider-track::-moz-range-thumb {
    width: 16px;
    height: 16px;
    background: white;
    border-radius: 8px;
    box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.25);
    cursor: pointer;
    border: none;
  }
</style>

<style>
  .style-select-content[data-slot='select-content'] {
    min-width: 100px !important;
    width: 100px !important;
  }

  .style-select-content [data-slot='select-viewport'] {
    width: 100px !important;
    min-width: 0 !important;
    padding: 2px 0 !important;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .style-select-item[data-slot='select-item'] {
    height: 34px;
    justify-content: center;
    padding: 0 !important;
    border-radius: 0;
  }

  .style-select-item > span.absolute {
    display: none;
  }

  .style-select-item > [data-slot='select-item-text'] {
    width: 100%;
    display: flex;
    justify-content: center;
  }
</style>
